import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { Line } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';
import { UsageWidget } from 'src/dashboard/Componets/UsageWidget';
import { EmptyLineOrBarChart } from 'src/common/components/Graphs/common/Graphs/EmptyLineOrBarChart';
import { Suspense, useState } from 'react';
import {
  UsageTypeSwitch,
  UsageType,
  UsageTypeValue,
} from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import {
  ChartTypeToggle,
  ChartType,
  ChartTypeValue,
} from 'src/common/components/ChartTypeToggle/ChartTypeToggle';
import { Widget } from 'src/dashboard/Componets/Widget';
import { AreaChartSkeleton } from 'src/common/components/Graphs/common/Graphs/components/AreaChartSkeleton';
import { ErrorBoundary } from 'react-error-boundary';
import { AreaChartSomethingWrong } from 'src/dashboard/Componets/AreaChartSomethingWrong';
import { pxToRem } from 'src/common/utils/fonts';

const AreaChartSkeletonContainer = styled(Box)(({ theme }) => ({
  height: `calc(100% - ${theme.spacing(20)})`,
}));

const StyledChartContainer = styled(Box)(() => ({
  height: '100%',
}));

type CommonChartWidgetProps = {
  accountRef: string | null;
  renderChart: (
    usageType: UsageTypeValue,
    chartType: ChartTypeValue,
    accountRef: string,
  ) => React.ReactNode;
};

export const ChartWithTypeAndUsageSelectWidget = ({
  accountRef,
  renderChart,
}: CommonChartWidgetProps) => {
  const { t } = useTranslation();

  const [usageType, setUsageType] = useState<UsageTypeValue>(UsageType.Data);
  const [chartType, setChartType] = useState<ChartTypeValue>(ChartType.Cumulative);

  const title = t('dashboard.cumulativeUsageChart.title');

  if (!accountRef) {
    return (
      <UsageWidget title={title}>
        <StyledChartContainer>
          <EmptyLineOrBarChart
            as={Line}
            emptyChartText={t('dashboard.placeholders.selectAccount')}
          />
        </StyledChartContainer>
      </UsageWidget>
    );
  }

  return (
    <Suspense
      fallback={
        <Widget title={title}>
          <AreaChartSkeletonContainer>
            <AreaChartSkeleton />
          </AreaChartSkeletonContainer>
        </Widget>
      }
    >
      <UsageWidget
        title={title}
        headerContent={
          <Box sx={{ marginRight: 'auto' }}>
            <UsageTypeSwitch value={usageType} onChange={setUsageType} />
          </Box>
        }
      >
        <StyledChartContainer role="figure" aria-label="Daily Usage">
          <ErrorBoundary
            fallback={<AreaChartSomethingWrong />}
            key={`${usageType}-usage-${chartType}`}
          >
            {renderChart(usageType, chartType, accountRef)}
          </ErrorBoundary>
          <Box sx={{ position: 'absolute', bottom: pxToRem(-10) }}>
            <ChartTypeToggle
              isOn={chartType === ChartType.NonCumulative}
              onToggle={() =>
                setChartType((p) =>
                  p === ChartType.Cumulative ? ChartType.NonCumulative : ChartType.Cumulative,
                )
              }
            />
          </Box>
        </StyledChartContainer>
      </UsageWidget>
    </Suspense>
  );
};
